import { useParams, useNavigate } from 'react-router-dom';
import { getWindmillJobDetailsApi } from '../../utils/apis';
import { useQuery } from '@tanstack/react-query';
import { Box, Typography, Button, Card } from '@mui/material';
import {
  ArrowLeftIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlayIcon,
  StopIcon,
} from '@phosphor-icons/react';
import JobDetailArea from '../../components/Windmill/JobDetailArea';
import { HeaderContainer } from '../../components/dashboard/AgentInfoHeader';
import { routes } from '../../constants/routes';
import LayoutWithSidebar from '../../components/common/layoutWithSidebar';
import { useDispatch } from 'react-redux';
import { clearWindmillJobId } from '../../utils/redux/slices/windmillFlowSlice';
import JobDetailSkeleton from '../../components/Windmill/JobDetailSkeleton';
import theme from '../../theme';
import NoDataFallback from '../../components/common/NoDataFallback';

const JobDetail = () => {
  const { flowPath, jobId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Convert hyphens back to forward slashes for the API call
  const originalPath = flowPath ? flowPath.replace(/-/g, '/') : '';

  const {
    data: jobDetails,
    isLoading: isJobDetailsLoading,
    isError: jobError,
  } = useQuery({
    queryKey: ['windmill-job-details', jobId],
    queryFn: () => getWindmillJobDetailsApi(jobId || ''),
    enabled: !!jobId,
  });

  // Clean the flow name by removing the u/quibbleaiio/ prefix
  const cleanFlowName = originalPath.replace(/^u\/quibbleaiio\//, '');

  const handleBackToFlow = () => {
    // Clear the selected job ID to prevent automatic re-navigation
    dispatch(clearWindmillJobId());
    const flowPathParam = flowPath || '';
    navigate(`${routes.quibbleFlow.replace(':flowPath', flowPathParam)}`);
  };

  const job = jobDetails?.data;

  // Helper functions
  const formatDuration = (durationMs: number) => {
    return `${(durationMs / 1000).toFixed(2)} sec`;
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const calculateEndTime = (startTime: string, durationMs: number) => {
    const start = new Date(startTime);
    const end = new Date(start.getTime() + durationMs);
    return end.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  if (isJobDetailsLoading) return <JobDetailSkeleton />;
  if (jobError)
    return (
      <NoDataFallback
        message="Error loading job details"
        description="There is an error loading the job details."
      />
    );

  return (
    <LayoutWithSidebar margin={0}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          bgcolor: '#FFFFFF',
        }}
      >
        {/* Header */}
        <HeaderContainer
          sx={{
            borderRadius: 0,
            border: 'none',
            borderBottom: '1px solid #DCE0E7',
            margin: 0,
            pb: 0,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Back button and title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                onClick={handleBackToFlow}
                sx={{
                  minWidth: 'auto',
                  p: 0,
                  color: 'text.primary',
                  '&:hover': { backgroundColor: 'transparent' },
                }}
              >
                <ArrowLeftIcon size={24} />
              </Button>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'DM Sans',
                    fontWeight: 600,
                    fontSize: '24px',
                    lineHeight: '100%',
                    letterSpacing: '-0.01em',
                    color: '#2B2D3F',
                    mb: 0.5,
                  }}
                >
                  Job Detail {jobId}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '14px',
                  }}
                >
                  Execution details for {cleanFlowName}
                </Typography>
              </Box>
            </Box>

            {/* Status Cards */}
            {job && (
              <Box sx={{ display: 'flex', gap: 2, pb: 2 }}>
                {/* Status Card */}
                <Card
                  sx={{
                    minWidth: 120,
                    borderRadius: '6px',
                    boxShadow: 'none',
                    border: `1px solid ${theme.palette.lightGray.main}`,
                    p: '16px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    {job.success ? (
                      <CheckCircleIcon size={14} color="#4CAF50" />
                    ) : (
                      <XCircleIcon size={14} color="#F44336" />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.primary',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      Status
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: '400',
                      fontSize: '14px',
                      color: job.success ? '#4CAF50' : '#F44336',
                    }}
                  >
                    {job.success ? 'Success' : 'Failed'}
                  </Typography>
                </Card>

                {/* Duration Card */}
                <Card
                  sx={{
                    minWidth: 140,
                    borderRadius: '6px',
                    boxShadow: 'none',
                    border: `1px solid ${theme.palette.lightGray.main}`,
                    p: '16px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <ClockIcon size={14} color="#2196F3" />
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.primary',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      Duration
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: '400',
                      fontSize: '14px',
                      color: '#2196F3',
                    }}
                  >
                    {job.duration_ms ? formatDuration(job.duration_ms) : 'N/A'}
                  </Typography>
                </Card>

                {/* Start Time Card */}
                <Card
                  sx={{
                    minWidth: 180,
                    borderRadius: '6px',
                    boxShadow: 'none',
                    border: `1px solid ${theme.palette.lightGray.main}`,
                    p: '16px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <PlayIcon size={14} color="#4CAF50" />
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.primary',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      Start Time
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: '400',
                      fontSize: '14px',
                      color: 'text.secondary',
                    }}
                  >
                    {job.created_at ? formatDateTime(job.created_at) : 'N/A'}
                  </Typography>
                </Card>

                {/* End Time Card */}
                <Card
                  sx={{
                    minWidth: 180,
                    borderRadius: '6px',
                    boxShadow: 'none',
                    border: `1px solid ${theme.palette.lightGray.main}`,
                    p: '16px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <StopIcon size={14} color="#FF5722" />
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.primary',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      End Time
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: '400',
                      fontSize: '14px',
                      color: 'text.secondary',
                    }}
                  >
                    {job.created_at && job.duration_ms
                      ? calculateEndTime(job.created_at, job.duration_ms)
                      : 'N/A'}
                  </Typography>
                </Card>
              </Box>
            )}
          </Box>
        </HeaderContainer>

        {/* Main Content Area - No Sidebar */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <JobDetailArea
            job={job}
            onBack={handleBackToFlow}
            isJobDetailsLoading={isJobDetailsLoading}
          />
        </Box>
      </Box>
    </LayoutWithSidebar>
  );
};

export default JobDetail;
